import {
  ARRIVAL_CONTENT_CLASS,
  ARRIVAL_ITEM_CLASS,
} from '@/shared/motion/arrival';
import type { MarkdownFilePaths } from '@/shared/components/Markdown';
import { TranscriptEntry } from './TranscriptEntry';
import { splitTranscriptPhases } from '../transcript';
import type { TranscriptEntry as TranscriptEntryData } from '../transcript';

export interface TranscriptEntryListProps {
  transcript: TranscriptEntryData[] | null | undefined;
  /**
   * Index of the first main-phase entry that arrived in the latest live batch
   * (spec 097 FR-006); those entries carry the arrival cue. `null` marks none.
   */
  arrivedFrom?: number | null;
  /** This execution's configured turn limit (spec 102), passed to each entry. */
  maxTurns?: number | null;
  /** How an edit's file is linked (spec 157), passed to each entry. */
  filePaths?: MarkdownFilePaths;
}

/**
 * One execution's transcript, in array order. Not virtualised: collapsed by
 * default and bounded by what one Claude session actually does, not by
 * unbounded historical growth (research.md §1) — unlike the outer executions
 * rail, which already is.
 *
 * The transition-selection phase, where present, renders as its own labeled
 * section after the main entries (research.md §2) — a separate `<ol>`, not a
 * divider inside one flat list, so the two are structurally distinct rather
 * than distinguished only by a visual break a reader could miss.
 */
export function TranscriptEntryList({
  transcript,
  arrivedFrom = null,
  maxTurns,
  filePaths,
}: TranscriptEntryListProps) {
  // Two different absences, and they used to render the same sentence (spec 089
  // FR-010). `null` means capture never ran or could not be recovered — the record is
  // lost. `[]` means it ran and there was nothing to record. Saying "unavailable" for
  // the second tells a reader the run is a mystery when it is not, and saying "nothing
  // happened" for the first is worse: it turns a lost record into a claim about the
  // run. The distinction exists in the column and survives all the way here.
  if (transcript == null) {
    return (
      <p
        className="text-muted-foreground text-sm"
        data-testid="transcript-state"
      >
        The transcript is unavailable for this run.
      </p>
    );
  }

  if (transcript.length === 0) {
    return (
      <p
        className="text-muted-foreground text-sm"
        data-testid="transcript-state"
      >
        This run recorded no activity.
      </p>
    );
  }

  const { main, transitionSelection } = splitTranscriptPhases(transcript);

  return (
    <div className="flex flex-col gap-4">
      <EntryList
        entries={main}
        arrivedFrom={arrivedFrom}
        testId="transcript-entries"
        maxTurns={maxTurns}
        filePaths={filePaths}
      />
      {transitionSelection ? (
        <div className="flex flex-col gap-3">
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Transition selection
          </p>
          <EntryList
            entries={transitionSelection}
            maxTurns={maxTurns}
            filePaths={filePaths}
          />
        </div>
      ) : null}
    </div>
  );
}

function EntryList({
  entries,
  arrivedFrom = null,
  testId,
  maxTurns,
  filePaths,
}: {
  entries: TranscriptEntryData[];
  arrivedFrom?: number | null;
  testId?: string;
  maxTurns?: number | null;
  filePaths?: MarkdownFilePaths;
}) {
  return (
    <ol className="flex flex-col gap-3" data-testid={testId}>
      {entries.map((entry, index) => {
        // Entries carry no id of their own; the array position is stable for
        // one execution's transcript, which only ever grows at the end — so an
        // existing item keeps its node (and a tool block its state) across a
        // live refresh, and only the new ones mount (spec 097 FR-005).
        const arrived = arrivedFrom !== null && index >= arrivedFrom;
        return (
          <li
            key={index}
            data-arrived={arrived ? 'true' : undefined}
            className={arrived ? ARRIVAL_ITEM_CLASS : undefined}
          >
            {arrived ? (
              <div className={ARRIVAL_CONTENT_CLASS}>
                <TranscriptEntry
                  entry={entry}
                  maxTurns={maxTurns}
                  filePaths={filePaths}
                />
              </div>
            ) : (
              <TranscriptEntry
                entry={entry}
                maxTurns={maxTurns}
                filePaths={filePaths}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
